import { useContext } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { css } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import AlertContext, { ALERT_STATUS } from "context/alert/alertContext";
import TutorialContext from "context/tutorial/tutorialContext";
import { FALSE, TRUE, TUTORIAL_INITIAL_RENDER } from "shared/constants/strings";
import { useMediaQueries } from "shared/hooks/useMediaQueries";
import { ROUTES } from "shared/types/enums";

import { BURGER_BUTTON } from "./constants";
import { STEP_GROUP, TOOLTIP_ACTIONS, TutorialTooltipProps } from "./types";

export const TutorialTooltip = ({
    step,
    tooltipProps,
    isLastStep,
    size
}: TutorialTooltipProps) => {
    const { gray700, white } = colors;

    const { setAlert } = useContext(AlertContext);

    const {
        setDrawerStatus,
        setTutorialOpen,
        setStepIndex,
        setInitialRender,
        isInitialRender,
        stepGroup,
        stepIndex
    } = useContext(TutorialContext);

    const { t } = useTranslation();
    const { toMd } = useMediaQueries();
    const navigate = useNavigate();

    const checkIfAllTutorialsAreDone = () => {
        const allTutorials = Object.values(STEP_GROUP).filter(currentStep => {
            return (
                currentStep !== STEP_GROUP.FinishTutorial &&
                currentStep !== STEP_GROUP.FirstTimeUser
            );
        });

        const areTutorialsFinished = allTutorials.every(
            tutorial => localStorage.getItem(tutorial) === TRUE
        );

        const isFinishTutorial = isInitialRender && areTutorialsFinished;

        setTutorialOpen(
            isFinishTutorial,
            0,
            isFinishTutorial
                ? STEP_GROUP.FinishTutorial
                : STEP_GROUP.TutorialCompanySelector
        );

        if (isInitialRender) {
            localStorage.setItem(TUTORIAL_INITIAL_RENDER, FALSE);
            setInitialRender(false);
        }

        return areTutorialsFinished;
    };

    const handleTutorialTaskGroupsClick = () => {
        if (toMd) {
            switch (stepIndex) {
                case 0:
                    setTutorialOpen(
                        false,
                        1,
                        STEP_GROUP.TutorialTaskGroups,
                        true
                    );

                    document.getElementById(BURGER_BUTTON)?.click();
                    setDrawerStatus(true);
                    break;
                case 1:
                    navigate(ROUTES.TaskGroups);
                    setDrawerStatus(false);
                    setStepIndex(stepIndex + 1);
                    localStorage.setItem(STEP_GROUP.TutorialTaskGroups, TRUE);
                    break;
                case 2:
                    checkIfAllTutorialsAreDone();
                    break;
                default:
                    setStepIndex(stepIndex + 1);
            }
        } else {
            if (stepIndex === 0) {
                navigate(ROUTES.TaskGroups);
                setStepIndex(stepIndex + 1);
                localStorage.setItem(STEP_GROUP.TutorialTaskGroups, TRUE);
                return;
            }

            checkIfAllTutorialsAreDone();
        }
    };

    const handleClick = () => {
        const isFirstTime = stepGroup === STEP_GROUP.FirstTimeUser;
        const isFinish = stepGroup === STEP_GROUP.FinishTutorial;
        const isTaskGroups = stepGroup === STEP_GROUP.TutorialTaskGroups;

        const isCompanySelector =
            stepGroup === STEP_GROUP.TutorialCompanySelector;

        if (isFirstTime && stepIndex === 0) {
            setTutorialOpen(true, 0, STEP_GROUP.TutorialCompanySelector);
            return;
        }

        if (isCompanySelector && stepIndex === 0) {
            setTutorialOpen(false, 1, undefined, true);
            localStorage.setItem(stepGroup, TRUE);
            return;
        }

        if (isCompanySelector && stepIndex === 1) {
            isInitialRender
                ? setTutorialOpen(true, 0, STEP_GROUP.TutorialTaskGroups)
                : setTutorialOpen(false, 0);

            return;
        }

        if (isTaskGroups) {
            handleTutorialTaskGroupsClick();
            return;
        }

        if (isFinish && stepIndex === 0) {
            setTutorialOpen(false, 0);

            setAlert({
                status: ALERT_STATUS.Success,
                title: t("Flash##tutorial completed")
            });

            return;
        }

        setStepIndex(stepIndex + 1);
    };

    const getButtonText = () => {
        if (isInitialRender) {
            if (stepGroup === STEP_GROUP.FirstTimeUser) {
                return TOOLTIP_ACTIONS.GetStarted;
            }

            if (stepGroup === STEP_GROUP.FinishTutorial) {
                return TOOLTIP_ACTIONS.Finish;
            }

            if (isLastStep && stepGroup !== STEP_GROUP.TutorialTaskGroups) {
                return TOOLTIP_ACTIONS.NextFeature;
            }

            return TOOLTIP_ACTIONS.Next;
        }

        return isLastStep ? TOOLTIP_ACTIONS.Finish : TOOLTIP_ACTIONS.Next;
    };

    return (
        <div
            css={css({
                padding: "16px",
                backgroundColor: gray700,
                maxWidth: "342px",
                width: "100%",
                borderRadius: "12px",
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0.1px",
                color: white
            })}
            {...tooltipProps}
        >
            <div
                css={css({
                    marginBottom: "8px",
                    fontWeight: 600,
                    display: "flex",

                    "span:first-of-type": {
                        marginRight: "auto"
                    }
                })}
            >
                <span>{step.title}</span>

                {stepGroup !== STEP_GROUP.FinishTutorial && (
                    <span>
                        {stepIndex + 1}/{size}
                    </span>
                )}
            </div>

            <div
                css={css({
                    marginBottom: "16px",
                    fontWeight: 400,
                    whiteSpace: "pre-line"
                })}
            >
                {step.content}
            </div>

            <Button
                color={BUTTON_COLORS.Secondary}
                size={BUTTON_SIZES.Normal}
                variant={BUTTON_VARIANTS.TextOnly}
                fullWidth
                onClick={handleClick}
            >
                {t(`Tutorial##buttons##${getButtonText()}`)}
            </Button>
        </div>
    );
};
